import type { DashboardCase, InvestorDashboard, PublishedUpdate } from '@fruuit/shared'
import {
  Badge,
  buttonClasses,
  Callout,
  Card,
  LoadingRegion,
  SkeletonText,
  StatGroup,
  StatTile,
  StatusBadge,
  statusLabel,
} from '@fruuit/ui'
import { ArrowUpRight, CalendarDays } from 'lucide-react'
import { Link } from 'react-router'
import { errorMessage } from '../../api/client'
import { dateTimeIn } from '../../features/calendar/time'
import { formatDate, plural, themeLabel, truncate } from '../../features/coverage/format'
import { UPDATE_LINK_LABEL, useInvestorDashboard } from '../../features/dashboard/api'
import { StageTrack } from '../../features/dashboard/StageTrack'
import { useTitle } from '../../lib/useTitle'

/** S1 Dashboard (investor): scope-filtered KPIs and what needs attention next (I-07, I-09). */
export function DashboardPage() {
  useTitle('Dashboard')
  const dashboard = useInvestorDashboard()
  return (
    <>
      <header className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold tracking-[var(--tracking-kicker)] text-teal-500 uppercase">
            Stewardship workspace
          </p>
          <h1 className="mt-2 font-display text-[28px] font-semibold leading-[1.2] text-ink sm:text-[34px]">
            Your portfolio, in focus.
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-sage sm:text-base">
            The latest decisions, evidence and next steps across your engagement programme.
          </p>
        </div>
        <Link to="/app/reports" className={buttonClasses()}>
          Prepare a report <ArrowUpRight aria-hidden="true" className="size-4" />
        </Link>
      </header>
      {dashboard.error ? (
        <Callout tone="danger" role="alert" title="The dashboard could not be loaded">
          {errorMessage(dashboard.error)}
        </Callout>
      ) : dashboard.data ? (
        <DashboardBody d={dashboard.data} />
      ) : (
        <LoadingRegion label="Loading dashboard">
          <SkeletonText lines={8} />
        </LoadingRegion>
      )}
    </>
  )
}

function DashboardBody({ d }: { d: InvestorDashboard }) {
  const k = d.kpis
  const na = d.need_attention
  return (
    <div className="space-y-6">
      <StatGroup>
        <StatTile
          label="Companies in coverage"
          value={k.companies_in_coverage}
          note={`${plural(k.holdings, 'holding')} across ${plural(k.portfolios, 'portfolio')}`}
          href="/app/portfolio"
        />
        <StatTile
          label="Open engagements"
          value={k.open_engagements}
          note={plural(k.objectives, 'company objective')}
          href="/app/engagements"
        />
        <StatTile
          label="Need attention"
          value={k.need_attention}
          note={`${plural(na.stalled_cases, 'stalled case')} · ${plural(na.mapping_exceptions, 'mapping exception')}`}
          href="#need-attention"
        />
        <StatTile
          label="Released records"
          value={k.released_records}
          note="Assessments, evidence reviews & correspondence"
          href="/app/updates"
        />
      </StatGroup>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card
          eyebrow="Review the change"
          title="Latest developments"
          actions={
            <Link to="/app/updates" className="font-semibold text-teal hover:underline">
              All published updates →
            </Link>
          }
        >
          {d.latest_updates.length === 0 ? (
            <p className="text-sm text-sage">No updates have been published yet.</p>
          ) : (
            <ol className="space-y-6">
              {d.latest_updates.map((u, index) => (
                <UpdateItem key={u.id} update={u} first={index === 0} />
              ))}
            </ol>
          )}
        </Card>
        <div className="space-y-6">
          <Card eyebrow="Next on the agenda" title="Upcoming meetings">
            {d.upcoming_meetings.length === 0 ? (
              <p className="text-sm text-sage">No announced meetings in this workspace yet.</p>
            ) : (
              <ul className="space-y-3">
                {d.upcoming_meetings.map((m) => {
                  const start = new Date(m.starts_at)
                  return (
                    <li key={m.id}>
                      <Link
                        to={`/app/meetings/${m.id}`}
                        className="flex items-center gap-3 rounded-md border border-line p-3 hover:bg-wash"
                      >
                        <span className="flex w-12 shrink-0 flex-col items-center rounded bg-teal-50 py-1 text-teal">
                          <b className="text-lg leading-none">{start.getUTCDate()}</b>
                          <small className="text-[11px] uppercase">
                            {start.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })}
                          </small>
                        </span>
                        <span className="min-w-0 text-sm">
                          <strong className="block truncate text-ink">{m.company}</strong>
                          <span className="block truncate text-sage">{m.title}</span>
                          <span className="block text-xs text-sage">
                            {dateTimeIn(m.starts_at, m.timezone)}
                          </span>
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
            <Link
              to="/app/calendar"
              className="mt-4 inline-block text-sm font-semibold text-teal hover:underline"
            >
              <CalendarDays aria-hidden="true" className="mr-1 inline size-4" />
              Open calendar →
            </Link>
          </Card>
          <Card eyebrow="Decisions & follow-up" title="Needs your attention">
            {d.attention_cases.length === 0 ? (
              <p className="text-sm text-sage">No open engagements need a decision right now.</p>
            ) : (
              <ul className="divide-y divide-line">
                {d.attention_cases.map((c) => (
                  <li key={c.id} className="py-3 first:pt-0 last:pb-0">
                    <Link to={`/app/cases/${c.id}`} className="block hover:underline">
                      <StatusBadge
                        status={c.status === 'stalled' ? 'stalled' : 'next action'}
                        label={c.status === 'stalled' ? 'Stalled' : 'Next action'}
                      />
                      <strong className="mt-1 block text-sm text-ink">{c.company}</strong>
                      <span className="block text-sm text-ink-soft">{c.next_action}</span>
                      <small className="text-xs text-sage">Due {formatDate(c.due_date)}</small>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card
          id="need-attention"
          eyebrow="Need attention"
          title="Stalled cases and mapping exceptions"
        >
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="font-semibold text-ink">{plural(na.stalled_cases, 'stalled case')}</h3>
              {na.stalled.length === 0 ? (
                <p className="text-sage">No case is stalled.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {na.stalled.map((c) => (
                    <li key={c.id}>
                      <Link to={`/app/cases/${c.id}`} className="text-teal hover:underline">
                        {c.company}: {c.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="font-semibold text-ink">
                {plural(na.mapping_exceptions, 'mapping exception')}
              </h3>
              {na.exceptions.length === 0 ? (
                <p className="text-sage">Every holding maps to a covered company.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {na.exceptions.map((e) => (
                    <li key={e.holding_id} className="text-ink-soft">
                      {e.identifier_value} · {e.portfolio} ·{' '}
                      <StatusBadge status={e.mapping_state} />
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to="/app/portfolio?mapping=exceptions"
                className="mt-2 inline-block font-semibold text-teal hover:underline"
              >
                View exceptions in the portfolio →
              </Link>
            </section>
            {na.review_overdue > 0 ? (
              <p className="text-sage">
                {plural(na.review_overdue, 'company', 'companies')} past their scheduled review.{' '}
                <Link
                  to="/app/portfolio?label=Review%20overdue"
                  className="font-semibold text-teal hover:underline"
                >
                  Show them →
                </Link>
              </p>
            ) : null}
          </div>
        </Card>
        <Card eyebrow="Your holdings" title="Alerts">
          {d.alerts.length === 0 ? (
            <p className="text-sm text-sage">
              No new findings, incidents or overdue reviews for your holdings.
            </p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {d.alerts.map((a) => (
                <li
                  key={`${a.kind}-${a.issuer_id}-${a.title}`}
                  className="flex items-start justify-between gap-3 py-2.5 first:pt-0"
                >
                  <span className="min-w-0">
                    <Link
                      to={`/app/companies/${a.issuer_id}`}
                      className="font-semibold text-teal hover:underline"
                    >
                      {a.company}
                    </Link>
                    <span className="block text-ink-soft">{a.title}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <Badge tone={a.kind === 'incident' ? 'watch' : 'attention'}>
                      {a.kind === 'adverse_finding'
                        ? 'New finding'
                        : a.kind === 'incident'
                          ? 'Incident'
                          : 'Review overdue'}
                    </Badge>
                    <small className="mt-1 block text-xs text-sage">{formatDate(a.date)}</small>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card
        eyebrow="Programme progress"
        title="From dialogue to evidence"
        actions={
          <Link to="/app/engagements" className="font-semibold text-teal hover:underline">
            All engagements →
          </Link>
        }
      >
        {d.programme.length === 0 ? (
          <p className="text-sm text-sage">No active engagements yet.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {d.programme.map((c) => (
              <ProgrammeCard key={c.id} c={c} />
            ))}
          </div>
        )}
        <p className="mt-5 text-xs leading-relaxed text-sage">
          Stages describe reviewed evidence against individual objectives. They do not measure
          investment performance or attributable real-world impact.
        </p>
      </Card>
    </div>
  )
}

function UpdateItem({ update: u, first }: { update: PublishedUpdate; first: boolean }) {
  const recordLink =
    u.kind === 'assessments' || !u.engagement_id
      ? `/app/companies/${u.issuer_id ?? ''}`
      : `/app/cases/${u.engagement_id}`
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className={
          first
            ? 'mt-1.5 size-2.5 shrink-0 rounded-full bg-teal'
            : 'mt-1.5 size-2.5 shrink-0 rounded-full border border-teal-300'
        }
      />
      <div className="min-w-0">
        <p className="text-xs text-sage">
          {u.company} · {formatDate(u.published_at)}
        </p>
        <h3 className="mt-0.5 font-semibold text-ink">{u.title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">{truncate(u.summary, 330)}</p>
        <p className="mt-1.5 flex flex-wrap gap-x-4 text-sm">
          <Link to={recordLink} className="font-semibold text-teal hover:underline">
            {UPDATE_LINK_LABEL[u.kind]}
          </Link>
          {u.engagement_id ? (
            <Link to={`/app/cases/${u.engagement_id}`} className="text-teal hover:underline">
              Engagement plan →
            </Link>
          ) : null}
        </p>
      </div>
    </li>
  )
}

function ProgrammeCard({ c }: { c: DashboardCase }) {
  return (
    <Link
      to={`/app/cases/${c.id}`}
      className="block rounded-md border border-line p-4 hover:bg-wash"
    >
      <p className="text-xs font-semibold tracking-[0.06em] text-teal-500 uppercase">
        {themeLabel(c.theme)}
      </p>
      <p className="mt-1 font-semibold text-ink">{c.company}</p>
      <p className="text-sm text-ink-soft">{c.title}</p>
      <div className="mt-3 space-y-1.5">
        {c.stages.map((stage, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: objectives in server order
          <StageTrack key={index} stage={stage} compact />
        ))}
      </div>
      <p className="mt-2 text-xs text-sage">
        {c.objectives_with_implementation}/{c.objectives} objectives with implementation evidence ·{' '}
        {statusLabel(c.status)}
      </p>
    </Link>
  )
}
